// FbrItemCell.js
// Register a custom Handsontable cell type "fbrItem"
// - Accepts a provided Handsontable instance OR uses module import fallback
// - Uses Vuex store + shared api for businessID and HTTP
// - Remote search + lazy loading
// - Stores rich object; renders "id - name"

import Handsontable from 'handsontable';
import store from '@/store';
import { api } from '@/utils/api';

export function registerFbrItemCellType(providedHT) {
  const HOT = providedHT || Handsontable;
  if (!HOT || !HOT.editors || !HOT.renderers) {
    // eslint-disable-next-line no-console
    console.error('[FbrItemCell] Handsontable module not available or incomplete.');
    return null;
  }

  const BaseEditor = HOT.editors.BaseEditor;

  // ---- utils ----
  const debounce = (fn, ms) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, a), ms);
    };
  };
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const gsx = () => globalThis.scrollX ?? window.scrollX ?? 0;
  const gsy = () => globalThis.scrollY ?? window.scrollY ?? 0;

  // ---- renderer: "id - name" ----
  function FbrItemRenderer(instance, TD, row, col, prop, value) {
    HOT.renderers.TextRenderer.apply(this, arguments);
    let display = '';
    if (value && typeof value === 'object') {
      const id = value.id != null ? String(value.id) : '';
      const name = value.name != null ? String(value.name) : '';
      display = id && name ? `${id} - ${name}` : id || name || '';
    } else if (value != null) {
      display = String(value);
    }
    TD.textContent = display;
    TD.title = display;
  }

  // ---- editor ----
  class FbrItemEditor extends BaseEditor {
    constructor(hot) {
      super(hot);

      // DOM
      this.container = null;
      this.input = null;
      this.list = null;
      this.loadingEl = null;
      this.emptyEl = null;

      // state
      this.items = [];
      this.query = '';
      this.pageNumber = 1;
      this.totalPages = 1;
      this.hasNext = false;
      this.loading = false;
      this.selectedIndex = -1;

      // config
      this.pageSize = 10;
      this.placeholder = 'Search items...';

      // deps (from your pointers)
      this.api = api;
      this.businessID = store?.state?.user?.businessID;

      // handlers
      this.onScrollHandler = this.onScroll.bind(this);
      this.onKeydownHandler = this.onKeydown.bind(this);
      this.onInputHandler = debounce(this.onInput.bind(this), 250);
      this.onClickOutside = this.handleOutsideClick.bind(this);
    }

    // ---- BaseEditor lifecycle ----
    createElements() {
      const doc = this.hot.rootDocument || document;

      this.container = doc.createElement('div');
      Object.assign(this.container.style, {
        position: 'absolute',
        zIndex: 10000,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        background: '#fff',
        width: '420px',
        maxHeight: '320px',
        display: 'none',
      });

      const wrap = doc.createElement('div');
      Object.assign(wrap.style, { display: 'flex', flexDirection: 'column', height: '100%' });

      this.input = doc.createElement('input');
      this.input.type = 'text';
      Object.assign(this.input.style, {
        padding: '10px 12px',
        border: 'none',
        borderBottom: '1px solid #eee',
        outline: 'none',
        fontSize: '14px',
      });

      this.list = doc.createElement('div');
      Object.assign(this.list.style, { overflowY: 'auto', flex: '1 1 auto' });

      this.loadingEl = doc.createElement('div');
      this.loadingEl.textContent = 'Loading...';
      Object.assign(this.loadingEl.style, {
        padding: '8px 12px',
        fontSize: '13px',
        color: '#666',
        display: 'none',
      });

      this.emptyEl = doc.createElement('div');
      this.emptyEl.textContent = 'No items found';
      Object.assign(this.emptyEl.style, {
        padding: '8px 12px',
        fontSize: '13px',
        color: '#666',
        display: 'none',
      });

      wrap.appendChild(this.input);
      wrap.appendChild(this.list);
      wrap.appendChild(this.loadingEl);
      wrap.appendChild(this.emptyEl);
      this.container.appendChild(wrap);
      (doc.body || document.body).appendChild(this.container);

      this.list.addEventListener('scroll', this.onScrollHandler);
      this.input.addEventListener('keydown', this.onKeydownHandler);
      this.input.addEventListener('input', this.onInputHandler);
      (doc || document).addEventListener('mousedown', this.onClickOutside);

      // minimal styles (scoped)
      const style = doc.createElement('style');
      style.textContent = `.fbr-item-row:hover{background:#f0f7ff!important}`;
      (doc.head || document.head).appendChild(style);
    }

    destroy() {
      const doc = this.hot.rootDocument || document;
      if (this.list) this.list.removeEventListener('scroll', this.onScrollHandler);
      if (this.input) {
        this.input.removeEventListener('keydown', this.onKeydownHandler);
        this.input.removeEventListener('input', this.onInputHandler);
      }
      doc.removeEventListener('mousedown', this.onClickOutside);
      if (this.container && this.container.parentNode)
        this.container.parentNode.removeChild(this.container);
      super.destroy();
    }

    getValue() {
      return this.__currentValue;
    }
    setValue(v) {
      this.__currentValue = v;
    }

    open() {
      // Ensure elements are created first
      if (!this.container || !this.list) {
        this.createElements();
      }

      // refresh deps in case user context changed
      this.api = api;
      this.businessID = store?.state?.user?.businessID;

      // reset
      this.items = [];
      this.query = '';
      this.pageNumber = 1;
      this.totalPages = 1;
      this.hasNext = false;
      this.loading = false;
      this.selectedIndex = -1;
      this.renderList();

      // position near TD with viewport clamp
      const TD = this.TD;
      const rect = TD.getBoundingClientRect();
      const vw = globalThis.innerWidth || document.documentElement.clientWidth;
      const vh = globalThis.innerHeight || document.documentElement.clientHeight;
      const desiredLeft = rect.left + gsx();
      const desiredTop = rect.top + gsy() + rect.height;
      const width = 420,
        height = 320;
      const left = clamp(desiredLeft, 8, gsx() + vw - width - 8);
      const top = clamp(desiredTop, 8, gsy() + vh - height - 8);

      Object.assign(this.container.style, {
        width: `${width}px`,
        maxHeight: `${height}px`,
        top: `${top}px`,
        left: `${left}px`,
        display: 'block',
      });

      this.input.value = '';
      this.input.placeholder = this.placeholder;
      this.input.focus();
      this.input.select();

      this.loadPage(1);
    }

    close() {
      if (this.container) this.container.style.display = 'none';
    }
    focus() {
      if (this.input) this.input.focus();
    }

    async loadPage(page) {
      if (this.loading) return;
      this.loading = true;
      this.showLoading(true);
      this.showEmpty(false);

      try {
        if (!this.api) throw new Error('api not available');
        if (!this.businessID) throw new Error('businessID not available');

        const params = {
          businessID: this.businessID,
          pageNumber: page,
          pageSize: this.pageSize,
          searchTerm: this.query || '',
        };

        const url = `/ItemsFBR/GetByPagination/${this.businessID}`;
        const res = await this.api.get(url, { params });
        const data = res?.data?.data;

        const newItems = Array.isArray(data?.items) ? data.items : [];
        const pg = data?.pagination || { totalPages: 1, currentPage: 1, hasNext: false };

        this.pageNumber = pg.currentPage || page;
        this.totalPages = pg.totalPages || 1;
        this.hasNext = !!pg.hasNext;

        if (page === 1) this.items = [];
        this.items = this.items.concat(newItems);

        this.renderList();
        if (this.items.length === 0) this.showEmpty(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[FbrItemCell] loadPage error:', e);
        this.items = [];
        this.renderList();
        this.showEmpty(true);
      } finally {
        this.showLoading(false);
        this.loading = false;
      }
    }

    renderList() {
      if (!this.list) return;

      const doc = this.hot.rootDocument || document;
      while (this.list.firstChild) this.list.removeChild(this.list.firstChild);

      this.items.forEach((it, idx) => {
        const row = doc.createElement('div');
        row.className = 'fbr-item-row';
        Object.assign(row.style, {
          padding: '8px 12px',
          cursor: 'pointer',
          borderBottom: '1px solid #f5f5f5',
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
        });

        const label = doc.createElement('div');
        label.textContent = `${it.id} - ${it.itemName || ''}`;
        label.style.whiteSpace = 'nowrap';
        label.style.overflow = 'hidden';
        label.style.textOverflow = 'ellipsis';

        const meta = doc.createElement('div');
        meta.textContent = it.meta_unit || '';
        meta.style.color = '#888';
        meta.style.fontSize = '12px';

        row.appendChild(label);
        row.appendChild(meta);

        row.addEventListener('mouseenter', () => this.highlight(idx));
        row.addEventListener('mouseleave', () => this.unhighlight(idx));
        row.addEventListener('mousedown', (e) => {
          e.preventDefault();
          this.pick(it);
        });

        this.list.appendChild(row);
      });
    }

    highlight(idx) {
      this.selectedIndex = idx;
      const ch = this.list.children;
      for (let i = 0; i < ch.length; i++) ch[i].style.background = i === idx ? '#f0f7ff' : '#fff';
    }
    unhighlight(idx) {
      const ch = this.list.children;
      if (ch[idx]) ch[idx].style.background = '#fff';
    }

    showLoading(show) {
      if (this.loadingEl) {
        this.loadingEl.style.display = show ? 'block' : 'none';
      }
    }
    showEmpty(show) {
      if (this.emptyEl) {
        this.emptyEl.style.display = show ? 'block' : 'none';
      }
    }

    onScroll() {
      if (!this.hasNext || this.loading || !this.list) return;
      const el = this.list;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (nearBottom) this.loadPage(this.pageNumber + 1);
    }

    onInput() {
      this.query = this.input.value.trim();
      this.pageNumber = 1;
      this.totalPages = 1;
      this.hasNext = false;
      this.loadPage(1);
    }

    onKeydown(e) {
      const maxIdx = this.items.length - 1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlight(clamp(this.selectedIndex + 1, 0, maxIdx));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlight(clamp(this.selectedIndex - 1, 0, maxIdx));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex <= maxIdx) {
          this.pick(this.items[this.selectedIndex]);
        } else if (this.items.length === 1) {
          this.pick(this.items[0]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.cancelChanges();
        this.close();
        this.finishEditing(false); // false = don't save changes
      }
    }

    pick(item) {
      console.log('PICKED:', JSON.stringify(item, null, 2));

      // Map to required value shape
      const mapped = {
        id: item.id,
        name: item.itemName, // API: itemName -> name
        HSCode: item.meta_HSCode,
        unit: item.meta_unit,
        unitID: item.unitID,
        tradePrice: item.tradePrice,
        retailPrice: item.retailPrice,
        salesTaxPercent: item.salesTaxPercent,
        addSalesTaxPercent: item.addSalesTaxPercent,
      };

      // Set the value in the editor
      this.setValue(mapped);

      // Update the cell data in Handsontable
      if (this.hot && typeof this.hot.setDataAtRowProp === 'function') {
        this.hot.setDataAtRowProp(this.row, this.prop, mapped);
      }

      this.close();
      this.finishEditing(true); // true = save changes
    }

    handleOutsideClick(e) {
      if (!this.container || this.container.style.display === 'none') return;
      if (!this.container.contains(e.target)) {
        this.cancelChanges();
        this.close();
        this.finishEditing(false); // false = don't save changes
      }
    }
  }

  const FbrItemCellType = { editor: FbrItemEditor, renderer: FbrItemRenderer };

  // Register
  if (HOT.cellTypes?.register) HOT.cellTypes.register('fbrItem', FbrItemCellType);
  else if (HOT.cellTypes?.registerCellType)
    HOT.cellTypes.registerCellType('fbrItem', FbrItemCellType);
  else {
    HOT.cellTypes = HOT.cellTypes || {};
    HOT.cellTypes['fbrItem'] = FbrItemCellType;
  }

  return FbrItemCellType;
}

export default registerFbrItemCellType;
